import React, {
  useState,
  useEffect,
  Children,
  useRef,
  ReactNode,
  useCallback,
  Suspense,
  Fragment,
} from "react";

import { push } from "react-router-redux";
import { connect } from "react-redux";

import {
  Col,
  Row,
  Menu,
  MenuProps,
  Avatar,
  Divider,
  Tooltip,
  Button,
  Flex,
  Tabs,
  Segmented,
  List,
  Skeleton,
  TreeSelect,
  Layout,
  Typography,
  Space,
  Dropdown,
  Mentions,
  Popconfirm,
  Spin,
} from "antd";
import {
  AppstoreOutlined,
  BellOutlined,
  CalendarFilled,
  CloseCircleOutlined,
  CloseOutlined,
  ContactsOutlined,
  FilterOutlined,
  MailOutlined,
  MessageOutlined,
  MoreOutlined,
  PaperClipOutlined,
  RobotOutlined,
  SendOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import ScrollableLoadMore from "../ScrollableLoadMore";
import session from "../../../../utils/session";
import human from "humanparser";
import _ from "lodash";
import {
  getActiveTime,
  parseNewLine,
  isValidPhoneNumberIntl,
} from "../helpers/ui";
import { uploadAIFile } from '../../resources';

const { Text } = Typography;

interface AIAssistantMessageInputFieldProps {
  goToCalendarIntegration?: () => void;
  // onSend can accept either a string (legacy) or a message object { text?: string, attachments?: Array<{name,url,type,size}> }
  onSend?: (message: any) => void;
  isSending?: boolean;
  conversationId?: string | number;
}

const AIAssistantMessageInputField: React.FC<AIAssistantMessageInputFieldProps> = ({ goToCalendarIntegration, onSend, isSending, conversationId }) => {
  // Send message handler
  const handleSend = () => {
    // Prevent sending while any attachment is still uploading
    const uploading = attachments.some(a => a.uploading);
    if (uploading) return;

    if (onSend) {
      const payload: any = { content: [] };
      if (message.trim()) {
        payload.content.push({ type: 'input_text', text: message.trim() });
      }
      if (attachments.length) {
        payload.attachments = attachments.map(a => ({ name: a.file.name, url: a.url, type: a.file.type, size: a.file.size }));
      }

      // Only send if there's text or attachments
      if ((payload.content && payload.content.length) || (payload.attachments && payload.attachments.length)) {
        onSend(payload);
        setMessage("");
        setAttachments([]);
      }
    }
  };
 
  const [userCanSendCalendarInvite, setUserCanSendCalendarInvite] = useState(
    false
  );
  const [sendCalendarPopUpContent, setSendCalendarPopUpContent] = useState({
    title: "Confirmation",
    description: "",
    okText: "Yes",
    cancelText: "No",
    onConfirm: () => setIsCalendarPopConfirmOpen(false),
    showCancel: true,
  });
  const [isCalendarPopConfirmOpen, setIsCalendarPopConfirmOpen] = useState(
    false
  );
  // Remove local isSending state, use prop instead
  const mentionRef = useRef(null);
  const [prefix, setPrefix] = useState("@");
  const [message, setMessage] = useState("");
  const [templateMenuItems, setTemplateMenuItems] = useState<any>([]);
  const [
    hasPriorSelectedMailMergeItem,
    setHasPriorSelectMailMergeItem,
  ] = useState(false);

  const [mailMerge, setMailMerge] = useState({});
  const messageMenuItems: MenuProps["items"] = [
    {
      key: "1",
      label: "Templates",
      icon: <PaperClipOutlined />,
      children: templateMenuItems,
    },
    {
      key: "2",
      label: "AI Agent Conversations",
      icon: <RobotOutlined />,
      // children: [
      //   {
      //     key: "2-1",
      //     label: "Test Message With Follow-ups",
      //   },
      //   {
      //     key: "2-2",
      //     label: "Interview Scheduling",
      //   },
      // ],
    },
  ];


  const onMailMergeSearch = (_, newPrefix) => {
    setPrefix(newPrefix);
  };

  function replaceCustomTags(input) {
    //this regex removes leading @{{ from words
    return input.replace(/@\{\{(\w+)\s?/g, "$1 ");
  }



  useEffect(() => {
    if (hasPriorSelectedMailMergeItem) {
      let currentText = mentionRef.current?.textarea?.value;
      setMessage(replaceCustomTags(currentText).trim());
      setHasPriorSelectMailMergeItem(false);
    }
  }, [hasPriorSelectedMailMergeItem]);

  // State to track focus/hover for box effect
  const [isBoxActive, setIsBoxActive] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState<Array<{ file: File; preview?: string; uploading?: boolean; url?: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload helper that sends files to API and returns url; lazy import resources to avoid cycles
  const uploadFiles = async (files: File[]) => {
  const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        // mark uploading
        setAttachments(prev => prev.map(a => a.file === file ? { ...a, uploading: true } : a));
    const res = await uploadAIFile(fd, conversationId, { headers: { 'Content-Type': 'multipart/form-data' } });
        // expecting API to return { data: { url: '...' } } or similar from api-core
        const url = res?.data?.url || res?.url || (res && typeof res === 'string' ? res : null);
        uploaded.push({ file, url });
        setAttachments(prev => prev.map(a => a.file === file ? { ...a, uploading: false, url } : a));
      } catch (err) {
        console.error('Upload failed', err);
        setAttachments(prev => prev.map(a => a.file === file ? { ...a, uploading: false } : a));
      }
    }
    return uploaded;
  };

  // Focus textarea when box is clicked
  const handleBoxClick = (e) => {
    // Prevent focusing if clicking a button
    if (
      e.target.tagName === "BUTTON" ||
      e.target.closest && e.target.closest("button")
    ) {
      return;
    }
    if (mentionRef.current) {
      mentionRef.current.focus();
    }
    setIsBoxActive(true);
  };

  // Handlers for focus/blur/hover
  const setActive = () => setIsBoxActive(true);
  const setInactive = () => setIsBoxActive(false);

  return (
    <Row justify="space-between" align="top" gutter={3}>
      <Col span={16} offset={4} style={{ background: "#fafafa" }}>
        <Spin spinning={isSending}>
          <div
            style={{
              background: "#fff",
              border: isBoxActive ? "1px solid #39b54a" : "1px solid #e4e4e4",
              borderRadius: "8px",
              padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              transition: "border-color 0.2s",
              cursor: "text",
            }}
            onMouseEnter={setActive}
            onMouseLeave={setInactive}
            onClick={handleBoxClick}
          >
            <textarea
              ref={mentionRef}
              style={{
                width: "100%",
                fontWeight: "normal",
                resize: "none",
                borderRadius: "6px",
                padding: "10px",
                fontSize: "16px",
                minHeight: "30px",
                lineHeight: "1",
                boxSizing: "border-box",
                outline: "none",
              }}
              placeholder="Ask me anything..."
              value={message}
              rows={3}
              disabled={isSending}
              onChange={e => setMessage(e.target.value)}
              onFocus={setActive}
              onBlur={setInactive}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
                // Shift+Enter will insert a newline by default
              }}
            />
            {/* Attachment previews */}
            {attachments.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {attachments.map((a, idx) => (
                  <div key={idx} style={{ border: '1px solid #eee', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {a.preview ? (
                      <img src={a.preview} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4 }} />
                    ) : (
                      <div style={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>{a.file.name}</div>
                    )}
                    <div style={{ fontSize: 12 }}>
                      <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.file.name}</div>
                      <div style={{ color: '#888' }}>{a.uploading ? 'Uploading...' : (a.url ? 'Uploaded' : 'Ready')}</div>
                    </div>
                    <Button size="small" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}><CloseOutlined /></Button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Tooltip title="Upload" placement="top">
                <Button
                  disabled={isSending}
                  icon={<PaperClipOutlined />}
                  onFocus={setActive}
                  onBlur={setInactive}
                  onMouseEnter={setActive}
                  onMouseLeave={setInactive}
                  onClick={() => fileInputRef.current?.click()}
                />
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  const withPreview = files.map(f => {
                    const isImage = f.type.startsWith('image/');
                    const preview = isImage ? URL.createObjectURL(f) : undefined;
                    return { file: f, preview };
                  });
                  setAttachments(prev => [...prev, ...withPreview]);
                  // start upload immediately
                  await uploadFiles(files);
                  // reset input
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              />
              <Tooltip title="Send Message" placement="top">
                <Button
                  disabled={isSending || !message.trim() || attachments.some(a => a.uploading)}
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  onFocus={setActive}
                  onBlur={setInactive}
                  onMouseEnter={setActive}
                  onMouseLeave={setInactive}
                />
              </Tooltip>
            </div>
          </div>
        </Spin>
      </Col>
    </Row>
  );
};

const mapDispatchToProps = (dispatch) => ({
  goToCalendarIntegration: () => {
    dispatch(push(`/interview-invites?tab=integrations`));
  },
});

export default connect(null, mapDispatchToProps)(AIAssistantMessageInputField);
